import { FIELD } from "./fieldKinds.js";
import { formatDateRange, formatLocation } from "../lib/format.js";
import { COMMON_EVENT_REGISTRATION_FIELDS } from "../features/registration/formConfig.js";
import {
  compose, coverSection, aboutSection, gallerySection, chipsSection,
  speakersSection, agendaSection, rsvpSection, subscribeSection, closingSection,
} from "../features/brochure/sections.js";

// Networking / meetup event type.
export default {
  id: "networking",
  category: "professional",
  label: "Networking / Meetup",
  blurb: "Meetups & mixers — hosts, agenda, RSVP.",
  icon: "🤝",
  wizardSteps: [
    {
      id: "basics",
      title: "The essentials",
      fields: [
        { key: "title", label: "Meetup title", kind: FIELD.TEXT, required: true, placeholder: "Founders & Coffee" },
        { key: "subtitle", label: "Tagline", kind: FIELD.TEXT, placeholder: "Monthly mixer for builders" },
        { key: "heroImage", label: "Hero image", kind: FIELD.HERO_IMAGE, previewPage: "cover" },
      ],
    },
    {
      id: "when-where",
      title: "When & where",
      fields: [
        { key: "startDate", label: "Date", kind: FIELD.DATE, required: true },
        { key: "endDate", label: "End date", kind: FIELD.DATE },
        { key: "venue", label: "Venue", kind: FIELD.TEXT },
        { key: "city", label: "City", kind: FIELD.TEXT },
        { key: "country", label: "Country", kind: FIELD.TEXT },
        { key: "mapsUrl", label: "Google Maps link", kind: FIELD.URL, placeholder: "https://maps.google.com/...", previewPage: "cover" },
      ],
    },
    {
      id: "details",
      title: "Details, contact & RSVP",
      fields: [
        { key: "description", label: "About the meetup", kind: FIELD.TEXTAREA, previewPage: "details" },
        { key: "registrationUrl", label: "RSVP / tickets link", kind: FIELD.URL, previewPage: "cover" },
        {
          key: "collectEventRegistrations",
          label: "Collect RSVPs inside einvite",
          kind: FIELD.TOGGLE,
          previewPage: "eventRegistration",
          description: "Adds an in-app RSVP form. Entries stay visible only to the creator.",
          defaultPatch: { eventRegistrationFields: COMMON_EVENT_REGISTRATION_FIELDS.map((field) => ({ ...field })) },
        },
        {
          key: "eventRegistrationFields",
          label: "RSVP form builder",
          kind: FIELD.REGISTRATION_FIELDS,
          previewPage: "eventRegistration",
          showWhen: (details) => !!details.collectEventRegistrations,
        },
        { key: "contacts", label: "Hosts", kind: FIELD.CONTACTS, previewPage: "closing" },
        {
          key: "collectPreRegistration",
          label: "Subscribe for updates",
          kind: FIELD.TOGGLE,
          previewPage: "subscribe",
          description: "A simple public form for attendees to get updates by mobile or email.",
        },
      ],
    },
    {
      id: "program",
      title: "Program",
      fields: [
        { key: "topics", label: "What we'll cover", kind: FIELD.CHIPS, placeholder: "Add a topic and press Enter", previewPage: "topics" },
        { key: "speakers", label: "Hosts / speakers", kind: FIELD.SPEAKERS, previewPage: "speakers" },
        { key: "schedule", label: "Agenda", kind: FIELD.SCHEDULE, itemLabel: "slot", previewPage: "schedule" },
      ],
    },
  ],
  planPages(event) {
    const details = event.details || {};
    return compose(
      coverSection(event, { ctaLabel: "RSVP →" }),
      aboutSection(details),
      gallerySection(details),
      chipsSection(details, { heading: "What we'll cover" }),
      speakersSection(details, { heading: "Hosts" }),
      agendaSection(details, "schedule", { title: "Agenda" }),
      rsvpSection(event),
      subscribeSection(event),
      closingSection(details, { ctaLabel: "RSVP →" }),
    );
  },
  deriveOg(details = {}) {
    const when = formatDateRange(details.startDate, details.endDate);
    const where = formatLocation(details);
    return {
      title: details.title || "A meetup",
      description: [details.subtitle, when, where].filter(Boolean).join(" · "),
    };
  },
};
